import { useEffect, useLayoutEffect, useRef, useState } from 'react'

// Streaming text arrives in uneven bursts (the model's chunks, then the
// server's 80 ms batching). Reveal it at a steady pace instead: each frame
// shows a share of what is still hidden, so the backlog clears in about
// CATCH_UP_MS whatever its size, never slower than MIN_CPS.

const CATCH_UP_MS = 200
const MIN_CPS = 40

/** Where to cut `text` so a half-written markdown link never shows as raw syntax. */
export function safeCut(text: string, n: number): number {
  const head = text.slice(0, n)
  const open = head.lastIndexOf('[')
  if (open < 0) return n
  const tail = head.slice(open)
  // A link is complete once its "(...)" has closed; plain "[...]" with no "(" after is just text.
  const complete = /^\[[^\]]*\]\([^)]*\)/.test(tail) || /^\[[^\]]*\](?!\()/.test(tail)
  if (complete) return n
  // An embed (`![…](…)`) is held back from its "!".
  return head[open - 1] === '!' ? open - 1 : open
}

function reducedMotion() {
  return (
    typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches
  )
}

/**
 * `text` revealed smoothly while it is `live` (still streaming) or still
 * catching up; text that was never live shows at once.
 */
export function useSmoothText(text: string, live: boolean): string {
  const [shown, setShown] = useState(() => (live && !reducedMotion() ? 0 : text.length))
  const target = useRef(text)
  useLayoutEffect(() => {
    target.current = text
  }, [text])
  const animating = shown < text.length

  useEffect(() => {
    if (!animating) return
    if (reducedMotion()) {
      setShown(target.current.length)
      return
    }
    let raf = 0
    let last = performance.now()
    let pos = shown
    const tick = (now: number) => {
      const dt = Math.min(64, now - last)
      last = now
      const backlog = target.current.length - pos
      const step = Math.max((backlog * dt) / CATCH_UP_MS, (MIN_CPS * dt) / 1000)
      pos = Math.min(target.current.length, pos + step)
      setShown(Math.floor(pos))
      if (pos < target.current.length) raf = requestAnimationFrame(tick)
    }
    raf = requestAnimationFrame(tick)
    return () => {
      cancelAnimationFrame(raf)
    }
    // Restart only when animation starts or stops; the loop reads the latest text itself.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [animating])

  if (!animating) return text
  return text.slice(0, safeCut(text, shown))
}
